<template>
  <div class="chat-container d-flex flex-column">
    <MessageList
      :messages="chatStore.groupedMessages"
      :features="features"
      :show-spinner="showAgentSpinner"
      :spinner-status="spinnerStatus"
      :active-block-status="activeBlockStatus"
    >
      <template v-if="$slots['empty-state']" #empty-state>
        <slot name="empty-state" />
      </template>
    </MessageList>
    <!-- isBusy, not isStreaming: the composer stays locked (and Stop stays
         available) while the turn is paused on a frontend tool. -->
    <MessageInput
      :is-streaming="chatStore.isBusy"
      :features="features"
      @send="onSend"
      @stop="stopStreaming"
      @new-chat="onNewChat"
    />
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import MessageList from './MessageList.vue'
import MessageInput from './MessageInput.vue'
import { useChatStore } from '../../stores/chat.js'
import { useSessionsStore } from '../../stores/sessions.js'
import { useChat } from '../../composables/useChat.js'
import { useI18n } from '../../composables/useI18n.js'

const props = defineProps({
  features: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['tool-call', 'tool-call-cancelled', 'new-chat'])

const chatStore = useChatStore()
const sessionsStore = useSessionsStore()
const { sendMessage, resumeWithToolInput, stopStreaming } = useChat()
const { t } = useI18n()

// With the tool timeline on, a trailing block carries its own live indicator
// (the ThinkingBlock header shimmers for the active block; the ReasoningBlock
// streams), so the loader would only duplicate it. A trailing reasoning block
// counts only when it actually renders (features.thinking on).
const trailingLiveBlock = computed(() => {
  if (!chatStore.showToolCalls) return false
  const items = chatStore.groupedMessages
  const last = items[items.length - 1]
  if (!last) return false
  if (last._thinkingBlock) return true
  if (last._reasoningBlock) return props.features?.thinking !== false
  return false
})

// Generic "agent is working" loader. Shown for the whole turn — including the
// pause on a frontend tool, when no stream is open but the host is still
// working or waiting on the user — until answer text streams or a live block
// takes over as the indicator. Keyed off isBusy, not isStreaming: the harness
// closes the /chat stream right after `frontend_tool_call`, so isStreaming
// alone goes dark exactly when the user most needs to see the turn is alive.
const showAgentSpinner = computed(() =>
  chatStore.isBusy &&
  !chatStore.streamingContent &&
  !trailingLiveBlock.value
)

// The agent's latest activity, shown next to the loader — the tool call's
// model-written summary (e.g. "Searching issues for water damage") or the
// latest line of reasoning, whichever is most recent. Respects features.thinking:
// with reasoning hidden, reasoning text is suppressed (and the label falls
// back to "Thinking…"), but tool summaries still show. While the turn is
// paused on a frontend tool the host's setStatus text wins, then the pending
// call's summary, then a generic "waiting for the dashboard" label — never
// "Thinking…", which would misdescribe a wait on the user.
const spinnerStatus = computed(() => {
  if (chatStore.pendingFrontendToolCall) {
    return chatStore.frontendToolStatus
      || chatStore.pendingFrontendToolSummary
      || t('chat.waitingForHost')
  }
  return chatStore.latestStatusLine(props.features?.thinking ?? true)
})

// Same pause status for the active ThinkingBlock header when the tool
// timeline is on (the loader hides behind a trailing block then).
const activeBlockStatus = computed(() =>
  chatStore.pendingFrontendToolCall ? spinnerStatus.value : ''
)

function onSend({ text, files }) {
  sendMessage(text, files)
}

function onNewChat() {
  chatStore.clear()
  sessionsStore.clear()
  emit('new-chat')
}

defineExpose({
  sendMessage,
  newChat: onNewChat,
  stop: stopStreaming,
})

// A payload answer is shown on the tool card as a short JSON preview; the model
// still receives the payload itself, uncut.
const TOOL_RESULT_PREVIEW_CHARS = 4000
function previewJson(value) {
  try {
    const json = JSON.stringify(value)
    if (typeof json !== 'string') return ''
    return json.length > TOOL_RESULT_PREVIEW_CHARS
      ? `${json.slice(0, TOOL_RESULT_PREVIEW_CHARS)}…`
      : json
  } catch {
    return ''
  }
}

// Surface frontend tool calls to the host via @tool-call. The host receives
// `{ toolCallId, name, args, respond, setStatus }` and MUST call
// respond({ result, error }) — or respond({ payload }) to send the model an
// exact object — to keep the chat loop moving; the chat protocol requires
// every frontend_tool_call to be answered. Until it does, the widget
// reports itself busy (loader, locked composer, Stop) and shows the host's
// setStatus text. Stop answers the call with a cancel error on the host's
// behalf and emits @tool-call-cancelled so the host can drop its own pending
// state. If no listener ever responds, the turn stays paused until the user
// hits Stop or starts a new chat.
watch(() => chatStore.pendingFrontendToolCall, (pending) => {
  if (!pending) return

  const sessionId = sessionsStore.activeSessionId
  let answered = false

  const respond = async ({ result = null, error = null, payload = undefined } = {}) => {
    if (answered) return
    answered = true

    chatStore.clearPendingFrontendToolCall()

    // `payload` goes to the model verbatim. Hosts need that: the frontend tool
    // descriptions promise shapes like { ok, result, logs } and
    // { error, problemCounts, samples }, and the narrower { result, error }
    // form silently dropped every field but those two. { result } / { error }
    // still work unchanged for every other handler.
    const hasPayload = payload !== undefined && payload !== null
    const payloadObject = hasPayload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload
      : null
    const displayError = error
      || (payloadObject && typeof payloadObject.error === 'string' ? payloadObject.error : null)

    if (displayError) {
      chatStore.setToolError({ toolCallId: pending.toolCallId, error: displayError })
    } else {
      chatStore.setToolResult({
        toolCallId: pending.toolCallId,
        result: typeof result === 'string' ? result : (hasPayload ? previewJson(payload) : ''),
      })
    }

    // Only resume the session the call belongs to. A late answer after the
    // user started a new chat (or switched sessions) must not stream the old
    // session's continuation into the new transcript.
    if (sessionId && sessionsStore.activeSessionId === sessionId) {
      // A host error must reach the model as { error: '...' } — posting null
      // here makes the server record "User cancelled the operation", which
      // hides the real rejection reason from the model entirely.
      await resumeWithToolInput(
        sessionId,
        pending.toolCallId,
        hasPayload ? payload : (error ? { error } : result),
      )
    }
  }

  const setStatus = (text) => chatStore.setFrontendToolStatus(text)

  // Answer first, then notify: the host's own teardown (e.g. rejecting its
  // proposal banner) typically calls respond too, which is then a no-op, so
  // the model always sees the same cancel result whichever side answered.
  chatStore.frontendToolCanceller = () => {
    if (answered) return
    respond({ error: 'User cancelled the operation' })
    emit('tool-call-cancelled', { toolCallId: pending.toolCallId, name: pending.name })
  }

  emit('tool-call', { ...pending, respond, setStatus })
})
</script>

<style scoped>
.chat-container {
  flex: 1 1 0;
  min-height: 0;
  /* Don't let wide message content (unbreakable chips) set our min width —
     stay at the host-given width and let inner content truncate instead. */
  min-width: 0;
}
</style>
